import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import {
  DueDiligenceAnswerType,
  statusColorToClasses,
} from 'shared/types/due-diligence';
import { getIconByValue } from 'components/dueDiligence/IconPicker';
import type { CategoryFormData, QuestionItem } from './types';
import type { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';

interface CategoryPreviewTabProps {
  formData: CategoryFormData;
  questions: QuestionItem[];
  fileLabels: DatabaseFileLabel[];
}

export const CategoryPreviewTab: React.FC<CategoryPreviewTabProps> = ({
  formData,
  questions,
  fileLabels,
}) => {
  const IconComponent = formData.icon ? getIconByValue(formData.icon) : null;
  const sortedQuestions = [...questions].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-base mb-1">Overzicht</h3>
        <p className="text-sm text-muted-foreground">
          Bekijk hoe de categorie eruitziet voordat je opslaat
        </p>
      </div>

      {/* Category summary */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            {IconComponent && (
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-moss-primary/10">
                <IconComponent weight="duotone" className="h-6 w-6 text-moss-primary" />
              </div>
            )}
            <div className="flex-1">
              <h4 className="text-lg font-semibold">{formData.name || '(Naamloos)'}</h4>
              {formData.description && (
                <p className="text-sm text-muted-foreground mt-1">{formData.description}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="secondary">
                  {formData.scope === 'building' ? 'Gebouw' : 'Unit'}
                </Badge>
                <Badge variant={formData.active ? 'default' : 'outline'}>
                  {formData.active ? 'Actief' : 'Inactief'}
                </Badge>
                <Badge variant="outline">{questions.length} {questions.length === 1 ? 'vraag' : 'vragen'}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions summary */}
      {sortedQuestions.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-muted-foreground">Vragen</h4>
          {sortedQuestions.map((question, index) => {
            const linkedLabels = fileLabels.filter((fl) =>
              question.documentLabelIds.some((id) => String(id) === String(fl.id)),
            );

            return (
              <Card key={question.id || index}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          {index + 1}.
                        </span>
                        <span className="text-sm font-medium">
                          {question.displayName || '(Naamloos)'}
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {question.answerType === DueDiligenceAnswerType.OPTIONS && 'Status'}
                          {question.answerType === DueDiligenceAnswerType.SIMPLE_SELECT && 'Selectie'}
                          {question.answerType === DueDiligenceAnswerType.DOCUMENT_EXISTS && 'Document'}
                          {question.answerType === DueDiligenceAnswerType.EXTRACTION && 'Extractie'}
                        </Badge>
                      </div>
                      {question.helpText && (
                        <p className="text-xs text-muted-foreground mt-1 ml-5">{question.helpText}</p>
                      )}

                      {/* Answer options preview */}
                      {question.answerType !== DueDiligenceAnswerType.EXTRACTION && question.answerOptions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2 ml-5">
                          {question.answerOptions.map((opt, optIdx) => (
                            <div key={opt.id || optIdx} className="flex items-center gap-1.5">
                              {question.answerType === DueDiligenceAnswerType.OPTIONS && (
                                <span
                                  className={`w-2.5 h-2.5 rounded-full ${statusColorToClasses[opt.statusColor]}`}
                                />
                              )}
                              <span className="text-xs text-muted-foreground">
                                {opt.label || '(leeg)'}
                              </span>
                              {opt.isDefault && (
                                <span className="text-[9px] text-moss-primary font-medium">(standaard)</span>
                              )}
                              {opt.conditionRoot && (
                                <span className="text-[9px] text-blue-600">
                                  ({opt.conditionRoot.children.length} voorwaarde{opt.conditionRoot.children.length !== 1 ? 'n' : ''})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Extraction info */}
                      {question.answerType === DueDiligenceAnswerType.EXTRACTION && (
                        <div className="mt-2 ml-5 text-xs text-violet-600">
                          {question.extractionLabel && <span>Label: {question.extractionLabel}</span>}
                          {question.extractionValueType && (
                            <span className="ml-2">
                              Type: {question.extractionValueType === 'number' ? 'Getal' :
                                question.extractionValueType === 'string' ? 'Tekst' :
                                question.extractionValueType === 'date' ? 'Datum' : 'Ja/Nee'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Linked labels */}
                      {linkedLabels.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2 ml-5">
                          {linkedLabels.map((fl) => (
                            <Badge key={fl.id} variant="outline" className="text-[10px] text-blue-600">
                              {fl.label}
                            </Badge>
                          ))}
                          {question.documentBehavior === 'cumulative' && (
                            <Badge variant="outline" className="text-[10px] text-violet-600">
                              Cumulatief
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {sortedQuestions.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Geen vragen geconfigureerd. Ga naar het &quot;Vragen&quot; tabblad om vragen toe te voegen.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
